import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import { computeMetersPerPixelAt, computeScaleBar } from '../utils/printScale';
import { EMPTY_VIEWER_CREDITS, readViewerCredits, viewerCreditsEqual, } from '../utils/viewerCredits';
import { setGoogleAttributionFocus } from '../utils/googleViewportAttribution';
// Furniture that sits ON the map window (Gaia-style): a north compass top-right,
// a graphic scale bar bottom-right, and the basemap credit bottom-left, so the
// printed imagery carries the same logos and copyright the live map shows (the
// Google and Cesium ion terms require them on static output). Fills its parent
// (which is positioned to the map-window rect). The exporter renders this as a
// separate layer ON TOP of the captured map image.
const INK = '#2f3027';
const PILL_BG = 'rgba(255, 255, 255, 0.88)';
const PILL_BORDER = 'rgba(62, 63, 54, 0.35)';
const NADIR_TOLERANCE_DEG = 1;
const CREDIT_SHADOW = '0 0 2px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.7)';
const COPYRIGHT_SEPARATOR = ' · ';
// Logos as Cesium lays them out on screen, then the copyright lines run
// together as one paragraph, white on the imagery like the live credit bar.
// Capped to the left half so it never runs under the scale bar pill.
const BasemapCredit = ({ credits }) => {
    if (credits.logosHtml.length === 0 && credits.copyright.length === 0)
        return null;
    return (_jsxs("div", { style: {
            position: 'absolute',
            bottom: 12,
            left: 12,
            maxWidth: '50%',
            color: '#fff',
            textShadow: CREDIT_SHADOW,
            fontSize: 9,
            lineHeight: 1.5,
        }, children: [credits.logosHtml.length > 0 && (_jsx("div", { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }, children: credits.logosHtml.map((html, i) => (_jsx("span", { className: "print-basemap-logo", style: { display: 'inline-flex', alignItems: 'center' }, dangerouslySetInnerHTML: { __html: html } }, i))) })), credits.copyright.length > 0 && (_jsx("div", { style: { paddingBottom: 2 }, children: credits.copyright.join(COPYRIGHT_SEPARATOR) }))] }));
};
const NorthArrow = ({ headingDeg }) => (_jsx("div", { style: { transform: `rotate(${-headingDeg}deg)`, width: 20, height: 25 }, children: _jsxs("svg", { width: "20", height: "25", viewBox: "0 0 30 38", children: [_jsx("text", { x: "15", y: "9", textAnchor: "middle", fontSize: "11", fontWeight: "700", fill: INK, children: "N" }), _jsx("polygon", { points: "15,12 8,32 15,27 22,32", fill: INK })] }) }));
const PrintMapOverlay = ({ viewer, frame, scaleSystem }) => {
    const windowCenter = { x: frame.x + frame.width / 2, y: frame.y + frame.height / 2 };
    const [headingDeg, setHeadingDeg] = useState(0);
    const [pitchDeg, setPitchDeg] = useState(-90);
    const [mpp, setMpp] = useState(null);
    const [credits, setCredits] = useState(EMPTY_VIEWER_CREDITS);
    const lastRef = useRef(0);
    // Track heading, scale and the basemap credits live as the user composes
    // (throttled). Credits are read after Cesium's own end-of-frame update.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const update = () => {
            const now = performance.now();
            if (now - lastRef.current < 150)
                return;
            lastRef.current = now;
            setHeadingDeg(Cesium.Math.toDegrees(viewer.camera.heading));
            setPitchDeg(Cesium.Math.toDegrees(viewer.camera.pitch));
            setMpp(computeMetersPerPixelAt(viewer, windowCenter.x, windowCenter.y));
            const next = readViewerCredits(viewer.container);
            setCredits((prev) => (viewerCreditsEqual(prev, next) ? prev : next));
        };
        update();
        const remove = viewer.scene.postRender.addEventListener(update);
        return () => remove();
    }, [viewer, windowCenter.x, windowCenter.y]);
    // The printout carries only what is inside the map window, not the whole
    // canvas behind the sheet.
    useEffect(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        setGoogleAttributionFocus(viewer, { x: frame.x, y: frame.y, width: frame.width, height: frame.height });
        return () => {
            if (!viewer.isDestroyed())
                setGoogleAttributionFocus(viewer, null);
        };
    }, [viewer, frame.x, frame.y, frame.width, frame.height]);
    // A graphic scale bar is only valid looking straight down.
    const isNadir = Math.abs(pitchDeg - -90) < NADIR_TOLERANCE_DEG;
    // Compact target so the on-map pill stays small (the title-block bar used 150).
    const scaleBar = isNadir ? computeScaleBar(mpp, scaleSystem, 80) : null;
    return (_jsxs("div", { style: {
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            fontFamily: 'Helvetica, Arial, sans-serif',
        }, children: [_jsx("div", { style: {
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    width: 32,
                    height: 32,
                    background: PILL_BG,
                    border: `1px solid ${PILL_BORDER}`,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                }, children: _jsx(NorthArrow, { headingDeg: headingDeg }) }), _jsx(BasemapCredit, { credits: credits }), scaleBar ? (_jsxs("div", { style: {
                    position: 'absolute',
                    bottom: 12,
                    right: 12,
                    background: PILL_BG,
                    border: `1px solid ${PILL_BORDER}`,
                    borderRadius: 8,
                    // Extra bottom padding absorbs html2canvas's residual baseline
                    // fudge so the label can't sit on (or under) the pill edge in the
                    // exported PDF. See the line-height note in PrintTitleBlock.
                    padding: '3px 8px 4px',
                    textAlign: 'center',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                }, children: [_jsx("div", { style: {
                            display: 'inline-block',
                            position: 'relative',
                            width: Math.round(scaleBar.widthPx),
                            height: 5,
                            borderLeft: `2px solid ${INK}`,
                            borderRight: `2px solid ${INK}`,
                            borderBottom: `2px solid ${INK}`,
                        }, children: _jsx("div", { style: {
                                position: 'absolute',
                                left: '50%',
                                bottom: 0,
                                height: 5,
                                borderLeft: `2px solid ${INK}`,
                                transform: 'translateX(-1px)',
                            } }) }), _jsx("div", { style: { fontSize: 10, color: INK, marginTop: 1, lineHeight: 1.5, textAlign: 'center', whiteSpace: 'nowrap' }, children: scaleBar.label })] })) : null] }));
};
export default PrintMapOverlay;
